import AuthAPI from '../api/auth';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import WindowVisibilityHelper from './AudioAlerts/WindowVisibilityHelper';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants';
import { hasConversationPermission } from './conversationPermissions';
import {
  PUSH_NOTIFICATION_FLAGS,
  canShowBrowserNotification,
  isInboxNotificationEnabled,
  resolveNewMessagePushFlag,
} from './notificationPreferences';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'aceleres.permissions.updated': this.onAccessProfileUpdated,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'team.changed': this.onTeamChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.created': this.onContactCreate,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'aceleres.reminders.updated': this.onRemindersUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'aceleres.equipe_refresh': this.onAceleresEquipeRefresh,
      'aceleres.carteirizacao_request.updated':
        this.onAceleresCarteirizacaoRequestUpdated,
      'aceleres.atendimento_request.updated':
        this.onAceleresAtendimentoRequestUpdated,
      'aceleres.wame_pool.updated': this.onAceleresWamePoolUpdated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = (data, event = '') => {
    const store = this.app.$store;
    if (store.getters.getCurrentAccountId !== data?.account_id) return false;
    const includesConversation =
      /^(?:conversation\.|message\.)/.test(event) ||
      [
        'assignee.changed',
        'team.changed',
        'first.reply.created',
        'copilot.message.created',
      ].includes(event) ||
      (event.startsWith('notification.') &&
        ['Conversation', 'Message'].includes(
          data?.notification?.primary_actor_type
        ));
    return (
      !includesConversation ||
      hasConversationPermission(
        store.getters.getCurrentUser,
        data.account_id,
        'conversation_view'
      )
    );
  };

  // Aceleres 2026-09-15: evento de inbox de e-mail nao e atendimento. So avisa
  // a aba E-mails da Caixa de Entrada (useEmailInbox), sem passar pelo store de
  // conversas -- que buscaria a conversa na rota de atendimento e a misturaria
  // nas caixas.
  isEmailInboxEvent = data => {
    const inboxId = data?.inbox_id || data?.conversation?.inbox_id;
    if (!inboxId) return false;
    const inbox = this.app.$store.getters['inboxes/getInbox']?.(inboxId);
    if (inbox?.channel_type !== 'Channel::Email') return false;

    emitter.emit(BUS_EVENTS.ACELERES_EMAIL_INBOX_UPDATED, {
      inboxId,
      conversationId: data?.conversation_id || data?.id,
    });
    return true;
  };

  onAccessProfileUpdated = data => {
    if (
      Number(data.user_id) !== Number(this.app.$store.getters.getCurrentUserID)
    ) {
      return;
    }
    emitter.emit('aceleres.access_profile.refresh', data);
  };

  onMessageUpdated = data => {
    if (this.isEmailInboxEvent(data)) return;
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
    emitter.emit(BUS_EVENTS.ACELERES_EQUIPE_ONLINE_REFRESH);
  };

  // eslint-disable-next-line class-methods-use-this
  onAceleresEquipeRefresh = () => {
    emitter.emit(BUS_EVENTS.ACELERES_EQUIPE_ONLINE_REFRESH);
  };

  // eslint-disable-next-line class-methods-use-this
  onAceleresCarteirizacaoRequestUpdated = data => {
    emitter.emit(BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onAceleresAtendimentoRequestUpdated = data => {
    emitter.emit(BUS_EVENTS.ACELERES_ATENDIMENTO_REQUEST_UPDATED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onAceleresWamePoolUpdated = data => {
    emitter.emit(BUS_EVENTS.ACELERES_WAME_POOL_UPDATED, data);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
      // Atualizar o registro não basta: se a conversa deixou de ser minha, ela
      // tem de sair da lista agora, não só no próximo F5 — e se passou a ser
      // minha, tem de entrar.
      this.app.$store.dispatch('evictConversationIfNoLongerMine', payload);
      this.app.$store.dispatch('addConversationIfNowMine', payload);
    }
    this.fetchConversationStats();
    this.maybeNotifyAssignedToMe(payload);
  };

  // Transferência de time (sem troca de assignee) também precisa recolocar a
  // conversa na lista/contador de quem ganhou ou perdeu acesso à equipe.
  onTeamChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  // Aceleres: alerta de lead atribuído ao usuário atual. Som e notificação são
  // validados separadamente contra as Preferências de Notificação — o som segue
  // os "Alertas de áudio" e a notificação de desktop segue a categoria
  // "Atribuição de conversa", a permissão do navegador e o filtro por canal.
  maybeNotifyAssignedToMe = payload => {
    try {
      const store = this.app.$store;
      const currentUserId = store.getters?.getCurrentUserID;
      const newAssigneeId = payload?.meta?.assignee?.id ?? payload?.assignee_id;

      if (!currentUserId || !newAssigneeId) return;
      if (Number(newAssigneeId) !== Number(currentUserId)) return;

      const inboxId = payload?.inbox_id;
      const conversationId = payload?.id ?? '';
      if (!isInboxNotificationEnabled(store, inboxId)) return;

      if (
        DashboardAudioNotificationHelper.isAudioAlertEnabledForEvent(
          EVENT_TYPES.ASSIGNED
        )
      ) {
        // Som sutil e único (ping), volume moderado
        const audio = new Audio('/audio/dashboard/ping.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      }

      if (
        canShowBrowserNotification(store, {
          flag: PUSH_NOTIFICATION_FLAGS.CONVERSATION_ASSIGNMENT,
          inboxId,
        })
      ) {
        // eslint-disable-next-line no-new
        new Notification('Novo lead atribuído', {
          body: 'Uma nova conversa foi atribuída a você.',
          icon: '/integrations/icons/aceleres.svg',
          tag: `aceleres-assigned-${conversationId}`,
          requireInteraction: true,
        });
      }
    } catch (e) {
      // silencioso — o alerta nunca deve quebrar o fluxo de atribuição
    }
  };

  onConversationCreated = data => {
    if (this.isEmailInboxEvent(data)) return;
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    // O payload do socket pode ter sido serializado antes da gravação dos
    // timestamps de leitura. Não o aplique sobre a conversa; a reconciliação
    // abaixo busca o estado já persistido e evita o marcador reaparecer.
    // A leitura deve substituir qualquer ajuste otimista por um snapshot
    // autoritativo e sem cache. Uma busca normal pode reintroduzir o contador
    // antigo logo depois de o marcador da conversa desaparecer.
    // Atualiza o badge localmente antes da reconciliação pesada no servidor.
    // O ChatList aplica o delta somente quando a conversa pertence à caixa
    // exibida para o usuário atual; a meta continua sendo a fonte final.
    emitter.emit('conversation_realtime_read', {
      conversationId: data.id,
      conversation: data,
    });
    this.fetchConversationStats({ force: true, immediate: true });
    // Reforço além do merge do payload do broadcast: busca de novo o estado
    // autoritativo da conversa (com unread_count calculado por aceleres_box
    // no backend), pra garantir que toda tela aberta (Novos/Outros/Meus)
    // reflita o vendedor tendo lido, mesmo se o payload do socket divergir.
    this.app.$store.dispatch('reconcileConversationPreview', data.id);
  };

  // O evento 'user:logout' significa "a tua sessao acabou", e nao "pede ao
  // servidor para a terminar". Quando ele chega, quem a terminou foi outra
  // aba — os tokens ja foram revogados, e por isso o DELETE /auth/sign_out
  // responde 404. Como `clearCookiesOnLogout` vivia so no `.then` do pedido,
  // a aba engolia a rejeicao e ficava aberta numa sessao morta, exatamente no
  // caso que este evento existe para cobrir.
  //
  // O pedido continua a ser feito (limpa a sessao do lado do servidor quando
  // ela ainda existe), mas a limpeza local acontece de qualquer maneira.
  // eslint-disable-next-line class-methods-use-this
  onLogout = () =>
    AuthAPI.logout().catch(() => {
      clearCookiesOnLogout();
    });

  onMessageCreated = data => {
    if (this.isEmailInboxEvent(data)) return;
    const conversationId = Number(
      data?.conversation_id || data?.conversation?.id || 0
    );
    const conversationData = data?.conversation || {};
    const { last_activity_at: lastActivityAt, unread_count: unreadCount } =
      conversationData;

    if (!conversationId) return;

    const previousConversation =
      this.app.$store.getters?.getConversationById?.(conversationId);
    const previousUnreadCount = Number(previousConversation?.unread_count || 0);
    const isIncomingMessage =
      data.private !== true &&
      Number(data.message_type) === MESSAGE_TYPE.INCOMING;

    if (isIncomingMessage) {
      this.clearContactTyping(conversationId);
    }
    // O backend atualiza a leitura do responsável antes de publicar uma
    // resposta humana, portanto o unread_count do evento já é autoritativo e
    // deve zerar o cartão imediatamente. Eventos legados/incompletos sem o
    // campo continuam preservando o valor local até a reconciliação.
    const hasAuthoritativeUnreadCount =
      unreadCount !== undefined &&
      unreadCount !== null &&
      Number.isFinite(Number(unreadCount));
    const effectiveUnreadCount = hasAuthoritativeUnreadCount
      ? Number(unreadCount)
      : previousUnreadCount;
    const messageForStore = data.conversation
      ? {
          ...data,
          conversation: {
            ...data.conversation,
            unread_count: effectiveUnreadCount,
          },
        }
      : data;

    DashboardAudioNotificationHelper.onNewMessage(data);
    this.maybeNotifyDesktopNewMessage(data);
    this.app.$store.dispatch('addMessage', messageForStore);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
      unreadCount: effectiveUnreadCount,
    });

    // A conversa pode ter saído da primeira página enquanto o atendente
    // estava em outra caixa. O evento continua válido, mas ADD_MESSAGE não
    // consegue inserir a mensagem sem um item correspondente no store.
    // Busca somente essa conversa, sem recarregar a SPA.
    const conversationInStore =
      this.app.$store.getters?.getConversationById?.(conversationId);
    if (!conversationInStore) {
      const conversationRefresh = this.app.$store.dispatch('getConversation', {
        id: conversationId,
        forceRefresh: true,
        respectAceleresBox: true,
      });
      if (conversationRefresh?.then) {
        conversationRefresh
          .then(() => {
            // O `addMessage` acima caiu no `if (!chat) return` do ADD_MESSAGE e
            // a mensagem foi descartada. Buscar a conversa traz o item para o
            // store, mas nao recupera essa mensagem: o payload da conversa so
            // carrega a ultima. Sem este reenvio, a conversa aparecia na lista e
            // o corpo da mensagem so surgia quando o usuario abria o chat.
            const chat =
              this.app.$store.getters?.getConversationById?.(conversationId);
            if (!chat) return;
            const jaTem = (chat.messages || []).some(
              existente => Number(existente?.id) === Number(data?.id)
            );
            if (jaTem) return;
            this.app.$store.dispatch('addMessage', data);
          })
          .catch(() => {});
      }
    }

    // O ChatList usa este evento para sincronizar cache, ordenação e a
    // contagem de não lidas em segundo plano, sem substituir a página inteira.
    emitter.emit('conversation_realtime_message', {
      conversationId,
      lastActivityAt,
      unreadCount: effectiveUnreadCount,
      message: data,
      previousUnreadCount,
    });

    if (isIncomingMessage) {
      // O contador enviado no payload de message.created pode ser produzido
      // antes de a reabertura/conclusão terminar de persistir o novo ciclo de
      // leitura. Reconcile depois do evento garante que a lista adote o
      // unread_count calculado pela API, sem recontar o histórico antigo.
      this.app.$store.dispatch('reconcileConversationPreview', conversationId);
      this.fetchConversationStats({ force: false, immediate: false });
    }
  };

  // Aceleres: notificação DESKTOP quando chega mensagem nova do cliente e a aba
  // NÃO está visível (segundo plano / sem foco). A exibição depende das
  // Preferências de Notificação do usuário: permissão do navegador ligada,
  // categoria correspondente marcada ("Nova mensagem em conversa atribuída" ou
  // "Criação de conversa", conforme o dono da conversa) e canal habilitado no
  // filtro por caixa de entrada. A tag por conversation_id faz dedupe (só a
  // mais recente por conversa fica visível).
  maybeNotifyDesktopNewMessage = data => {
    try {
      if (WindowVisibilityHelper.isWindowVisible()) return;
      if (data.private === true) return; // ignora notas privadas
      if (Number(data.message_type) !== MESSAGE_TYPE.INCOMING) return; // só do cliente

      const store = this.app.$store;
      const flag = resolveNewMessagePushFlag({
        assigneeId: data?.conversation?.assignee_id,
        currentUserId: store.getters?.getCurrentUserID,
      });

      if (
        !canShowBrowserNotification(store, { flag, inboxId: data?.inbox_id })
      ) {
        return;
      }

      const senderName =
        data.sender?.name ||
        data.conversation?.meta?.sender?.name ||
        'Nova mensagem';
      const preview = String(data.content || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);

      // eslint-disable-next-line no-new
      new Notification(senderName, {
        body: preview || 'Você recebeu uma nova mensagem.',
        icon: '/integrations/icons/aceleres.svg',
        tag: `aceleres-msg-${data.conversation_id || ''}`,
      });
    } catch (_) {
      // silencioso — a notificação nunca deve quebrar o fluxo de mensagens
    }
  };

  // As atualizações de conversa já chegam como eventos individuais. Recarregar
  // toda a SPA aqui descarta a lista e a conversa aberta sem necessidade.
  // eslint-disable-next-line class-methods-use-this
  onReload = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onStatusChange = data => {
    if (this.isEmailInboxEvent(data)) return;
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    if (this.isEmailInboxEvent(data)) return;
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  // Aceleres 2026-09-04: num grupo quem digita e o participante, mas o `user`
  // do evento e o contato da conversa -- o grupo. O nome vem a parte, em
  // `typing_participant_name`, e viaja grudado no registro da store para o
  // cartao poder escrever "Vanessa esta digitando...".
  onTypingOn = ({
    conversation,
    user,
    typing_participant_name: participant,
  }) => {
    const conversationId = conversation.id;
    const nome = String(participant || '').trim();
    const typingUser = nome ? { ...user, typingParticipantName: nome } : user;

    this.clearTimer(conversationId);
    // A store deduplica por id+tipo, entao um segundo evento do MESMO grupo com
    // outro participante seria descartado e o rotulo ficaria no nome antigo.
    // Trocar quem digita e trocar o registro.
    const anterior = (
      this.app.$store.getters?.['conversationTypingStatus/getUserList']?.(
        conversationId
      ) || []
    ).find(record => record?.id === user?.id && record?.type === user?.type);
    if (anterior && (anterior.typingParticipantName || '') !== nome) {
      this.app.$store.dispatch('conversationTypingStatus/destroy', {
        conversationId,
        user: anterior,
      });
    }

    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user: typingUser,
    });
    this.initTimer({ conversation, user: typingUser });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  // Quem manda a mensagem parou de digitar -- e no WhatsApp foi ENVIANDO que
  // parou. Os tres pontos ficavam ate 15s ao lado da mensagem ja entregue,
  // porque so os apagava um `typing_off` explicito ou o relogio de seguranca:
  // medido na producao AWS em 04/09/2026, um contato passou 108s digitando sem
  // nenhum `typing_off`, e a mediana do desligamento foi 4,06s com pior caso de
  // 7,63s. A WAME nao garante o evento de parada do contato (ver ARMADILHAS 11),
  // entao a chegada da mensagem e o sinal mais confiavel que existe.
  //
  // So os do OUTRO lado (`type !== 'user'`), que sao os que acendem a bolha em
  // MessagesView: o "digitando" do proprio atendente e desligado pelo editor
  // dele, 2s depois da ultima tecla.
  // Nunca pode derrubar o tratamento da mensagem: apagar a bolha e acessorio,
  // exibir a mensagem nao e. Um teste pegou exatamente isso -- store sem o
  // getter fazia a chamada estourar e o resto de onMessageCreated (inclusive
  // carregar a conversa que faltava na lista) nunca rodava.
  clearContactTyping = conversationId => {
    try {
      const lista =
        this.app.$store.getters?.['conversationTypingStatus/getUserList'];
      if (typeof lista !== 'function') return;

      const typers = lista(conversationId) || [];

      typers
        .filter(user => user?.type !== 'user')
        .forEach(user =>
          this.onTypingOff({ conversation: { id: conversationId }, user })
        );
    } catch (error) {
      // Sem bolha apagada o relogio de seguranca ainda cobre o caso.
    }
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Este relógio é REDE DE SEGURANÇA, não a duração do indicador: quem
    // desliga o "digitando" é o `typing_off`, que o WhatsApp manda assim que o
    // contato para (via `paused`/`available`).
    //
    // Estava em 3s para contato e isso apagava o indicador de quem CONTINUAVA
    // digitando: o WhatsApp reemite `composing` a cada ~10s, então a bolha
    // acendia 3s e ficava 7s apagada. 15s cobre esse intervalo com folga e
    // ainda limpa sozinho se o `typing_off` se perder.
    const typingTimeout = user?.type === 'user' ? 30000 : 15000;
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, typingTimeout);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = (options = {}) => {
    emitter.emit('fetch_conversation_stats', options);
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onContactCreate = data => {
    this.app.$store.dispatch('contacts/setContact', data);
  };

  onRemindersUpdated = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit(BUS_EVENTS.ACELERES_REMINDERS_UPDATED, data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
    emitter.emit(BUS_EVENTS.INBOX_NOTIFICATION_UPDATED, data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
    emitter.emit(BUS_EVENTS.INBOX_NOTIFICATION_UPDATED, data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
    emitter.emit(BUS_EVENTS.INBOX_NOTIFICATION_UPDATED, data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
